import { Table, Tag } from "antd";
import React, { useMemo, useState } from "react";
import { i18n } from "~/i18n.js";
import { ActualFlightTime } from "~/components/clock/actualFlightTime.jsx";
import { LandingIcon } from "~/components/icon/icon.jsx";
import { defaultTableConfig } from "~/components/layout/table.jsx";
import {
  OGNlandingsTijd,
  OGNstartTijd,
} from "~/components/flight/sections/Ogn.jsx";
import { $t } from "~/i18n.js";
import { parseMinutes } from "~/lib/localize.js";
import { FlightMotorTime } from "~/components/form/FlightMotorTime.jsx";
import { SwitchYesNo } from "~/components/form/SwitchYesNo.jsx";

const OntbreektTag = () => (
  <Tag color="red" className="bounce">
    {$t("ontbreekt")}
  </Tag>
);

const zweefkist = (flight_lookup, vliegerLookup, isDebug) => ({
  title: $t("Zweefkist"),
  render: (_, flight) => {
    let zweefVlucht = null;

    // if flight is missing sleep, then we actually have the sleep flight
    if (flight.missing_sleep) {
      zweefVlucht = flight;
    } else if (!flight.start_methode.endsWith("-a")) {
      return <Tag>{flight.start_methode}</Tag>;
    } else {
      zweefVlucht = flight_lookup[flight.sleep_uuid];
    }

    // lookup zweefvlucht en show the data
    if (!zweefVlucht || !zweefVlucht.registratie) return OntbreektTag();

    return (
      <p>
        {zweefVlucht.callsign} {zweefVlucht.registratie} <br />
        {zweefVlucht.type}
        {isDebug && (
          <>
            <br />
            <span className="gray">{zweefVlucht.id}</span>
          </>
        )}
        <br />
        {zweefVlucht.gezagvoerder_naam ? (
          <span>{zweefVlucht.gezagvoerder_naam}</span>
        ) : (
          OntbreektTag()
        )}
      </p>
    );
  },
});

const Tijden = () => ({
  title: $t("Sleep Tijd"),
  width: 90,
  render: (_, flight) => (
    <div>
      {flight.start_tijd && (
        <OGNstartTijd
          ogn_indicator={flight.start_ogn}
          time={flight.start_tijd}
        />
      )}
      {!flight.missing_sleep && flight.landings_tijd && (
        <OGNlandingsTijd
          marginLeft={0}
          ogn_indicator={flight.landings_ogn}
          time={flight.landings_tijd}
        />
      )}
    </div>
  ),
});

const SleepColumns = (
  flight_lookup,
  sleep_category,
  vliegerLookup,
  isDebug
) => {
  const columns = [
    {
      title: $t("Sleepkist"),
      render: (_, flight) => {
        if (flight.missing_sleep) return OntbreektTag();
        if (!flight.registratie) return OntbreektTag();
        return (
          <p>
            {flight.callsign} {flight.registratie} <br />
            {flight.type}
            {isDebug && (
              <>
                <br />
                <span className="gray">{flight.id}</span>
              </>
            )}
            <br />
            {flight.gezagvoerder_naam ? (
              <span>{flight.gezagvoerder_naam}</span>
            ) : (
              OntbreektTag()
            )}
          </p>
        );
      },
    },
    zweefkist(flight_lookup, vliegerLookup, isDebug),
    Tijden(),
    {
      title: $t("Duur"),
      dataIndex: "vluchtduur",
      width: 70,
      render: (text, flight) => {
        if (flight.missing_sleep) return null;
        if (flight.landings_tijd)
          return (
            <div
              className="column"
              style={{ gap: 4, alignItems: "flex-start" }}
            >
              <span>{parseMinutes(text)}</span>
              {flight.motor_minutes > 0 && (
                <Tag>
                  <FlightMotorTime motorMinutes={flight.motor_minutes} />
                </Tag>
              )}
            </div>
          );
        if (flight.start_tijd) return <ActualFlightTime flight={flight} />;
      },
    },
    {
      title: $t("Hoogte"),
      dataIndex: "height",
      width: 80,
      render: (value, flight) => {
        if (flight.missing_sleep) return null;
        return i18n.parseLocaleHeightString(value);
      },
    },
    {
      title: sleep_category ? sleep_category.split(",")[0] : "",
      dataIndex: "category",
      width: null,
      render: (_, flight) => (
        <div className="row" style={{ gap: 6 }}>
          
          {flight.is_deleted && <Tag color="red">{$t("verwijderd")}</Tag>}

          {flight.category && <Tag>{flight.category}</Tag>}
          {flight.vertrek_vliegveld &&
            flight.aankomst_vliegveld &&
            flight.vertrek_vliegveld !== flight.aankomst_vliegveld && (
              <Tag color="green">
                <LandingIcon size={12} />
                <span style={{ marginLeft: 4 }}>
                  {flight.aankomst_vliegveld}
                </span>
              </Tag>
            )}
        </div>
      ),
    }
  ];
  return columns;
};

export const SleepTable = ({
  allFlights,
  sleep_cat,
  setActiveSleep,
  vliegers,
  sleep_kisten,
}) => {
  const vliegerLookup = useMemo(
    () =>
      vliegers &&
      vliegers.reduce((all, v) => {
        all[v.id] = v.name;
        return all;
      }, {}),
    [vliegers]
  );

  const numberDeletedFlights = allFlights.filter((f) => f.is_deleted).length;
  const [showDeletedFlights, setShowDeletedFlights] = useState(false);

  const isDebug = window.location.hostname.endsWith("dev.localhost");
  isDebug && console.log("DEBUGGING MODE IS ON");

  const filterFlights = (_flights, _sleepkisten, _showDeletedFlights) => {
    const kistLookup =
      _sleepkisten &&
      _sleepkisten.reduce((all, k) => {
        all[k.id] = k.id;
        return all;
      }, {});

    const sleepFlights = [];
    const zweefFlightLookup = {};

    if (!_showDeletedFlights) {
      _flights = _flights.filter((f) => !f.is_deleted);
    }

    _flights &&
      _flights.forEach((flight) => {
        if (flight.start_methode == "sleep" && !flight.sleep_uuid) {
          sleepFlights.push({ ...flight, missing_sleep: true });
        } else if (
          ["tmg", "sep", "tmg-a", "sep-a"].includes(flight.start_methode) ||
          (kistLookup && flight.kist_id in kistLookup)
        ) {
          sleepFlights.push(flight);
        } else {
          zweefFlightLookup[flight.uuid] = flight;
        }
      });
    sleepFlights.sort((a, b) => b.start_tijd_int - a.start_tijd_int);
    return { sleepFlights, zweefFlightLookup };
  };

  const { sleepFlights, zweefFlightLookup } = useMemo(
    () => filterFlights(allFlights, sleep_kisten, showDeletedFlights),
    [allFlights, sleep_kisten, showDeletedFlights]
  );

  return (
    <div>
      {numberDeletedFlights > 0 && (
        <div className="small" style={{ padding: "8px 0" }}>
          <span style={{ marginRight: 8, marginLeft: 8 }}>
            {$t("Toon ${count} verwijderde vluchten:", {
              count: numberDeletedFlights,
            })}
          </span>
          <SwitchYesNo
            size="small"
            value={showDeletedFlights}
            onChange={setShowDeletedFlights}
          />
        </div>
      )}
      <Table
        // loading={loading}
        style={{ marginTop: 12, width: "100%", overflowX: "auto" }}
        rowKey="id"
        pagination={defaultTableConfig}
        className="table--clickable"
        size="small"
        dataSource={sleepFlights}
        columns={SleepColumns(
          zweefFlightLookup,
          sleep_cat,
          vliegerLookup,
          isDebug
        )}
        onRow={(flight) => {
          return {
            onClick: () => setActiveSleep(flight),
          };
        }}
      />
    </div>
  );
};
